import React from 'react';
import { Container, Row, Col, Card, Button, Badge, ProgressBar } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import Slide from 'react-reveal/Slide';
import './styles/projects.css';

const projectsData = [
    {
        id: 1,
        title: "Metro Manila PASADA Pilot Program",
        description: "Large-scale implementation of PASADA system across 15 major routes in Metro Manila, serving over 500,000 daily passengers.",
        location: "Metro Manila, Philippines",
        status: "Completed",
        progress: 100,
        duration: "18 months",
        beneficiaries: "500,000+ passengers",
        vehicles: "2,500 vehicles",
        image: "https://via.placeholder.com/400x250/28a745/ffffff?text=Metro+Manila",
        category: "Pilot Program",
        startDate: "January 2023",
        endDate: "June 2024"
    },
    {
        id: 2,
        title: "Cebu Smart Transportation Network",
        description: "Integration of smart transportation technology in Cebu's public transport system with focus on tourism routes.",
        location: "Cebu City, Philippines",
        status: "Active",
        progress: 75,
        duration: "24 months",
        beneficiaries: "300,000+ passengers",
        vehicles: "1,800 vehicles",
        image: "https://via.placeholder.com/400x250/007bff/ffffff?text=Cebu+Project",
        category: "Smart City Initiative",
        startDate: "March 2023",
        endDate: "March 2025"
    },
    {
        id: 3,
        title: "Davao Sustainable Transport Project",
        description: "Eco-friendly transportation solution promoting electric jeepneys and buses with solar charging stations.",
        location: "Davao City, Philippines",
        status: "Active",
        progress: 60,
        duration: "30 months",
        beneficiaries: "250,000+ passengers",
        vehicles: "1,200 vehicles",
        image: "https://via.placeholder.com/400x250/17a2b8/ffffff?text=Davao+Project",
        category: "Sustainability Project",
        startDate: "June 2023",
        endDate: "December 2025"
    },
    {
        id: 4,
        title: "Rural Connectivity Initiative",
        description: "Bringing modern transportation technology to rural areas with offline-capable mobile applications.",
        location: "Provincial Philippines",
        status: "Planning",
        progress: 25,
        duration: "36 months",
        beneficiaries: "150,000+ passengers",
        vehicles: "800 vehicles",
        image: "https://via.placeholder.com/400x250/6c757d/ffffff?text=Rural+Project",
        category: "Rural Development",
        startDate: "January 2024",
        endDate: "January 2027"
    },
    {
        id: 5,
        title: "University Campus Transport",
        description: "Campus-wide transportation management system for universities with student ID integration.",
        location: "Multiple Campuses",
        status: "Beta Testing",
        progress: 85,
        duration: "12 months",
        beneficiaries: "50,000+ students",
        vehicles: "200 vehicles",
        image: "https://via.placeholder.com/400x250/ffc107/ffffff?text=Campus+Project",
        category: "Education Sector",
        startDate: "August 2023",
        endDate: "August 2024"
    },
    {
        id: 6,
        title: "Disaster Response Transport Network",
        description: "Emergency transportation coordination system for disaster response and evacuation procedures.",
        location: "Nationwide",
        status: "Development",
        progress: 40,
        duration: "18 months",
        beneficiaries: "Emergency Response",
        vehicles: "500 vehicles",
        image: "https://via.placeholder.com/400x250/dc3545/ffffff?text=Emergency+Response",
        category: "Emergency Response",
        startDate: "October 2023",
        endDate: "April 2025"
    }
];

const Projects = () => {
    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return 'success';
            case 'Active': return 'primary';
            case 'Beta Testing': return 'warning';
            case 'Development': return 'info';
            case 'Planning': return 'secondary';
            default: return 'secondary';
        }
    };

    const getProgressColor = (progress) => {
        if (progress >= 80) return 'success';
        if (progress >= 60) return 'info';
        if (progress >= 40) return 'warning';
        return 'danger';
    };

    return (
        <div className="projects-page">
            <Helmet>
                <title>Projects - PASADA</title>
                <meta name="description" content="Explore PASADA's ongoing and completed transportation projects across the Philippines" />
                <link rel="canonical" href="/projects" />
            </Helmet>

            {/* Hero Section */}
            <section className="projects-hero">
                <Container>
                    <Row className="align-items-center min-vh-60">
                        <Col lg={8} md={10} className="mx-auto text-center">
                            <Slide bottom>
                                <h1 className="hero-title">Our Projects</h1>
                                <p className="hero-subtitle">
                                    Transforming transportation infrastructure across the Philippines through innovative technology solutions
                                </p>
                                <div className="stats-row mt-5">
                                    <div className="stat-item">
                                        <h3>1M+</h3>
                                        <p>Passengers Served</p>
                                    </div>
                                    <div className="stat-item">
                                        <h3>6,000+</h3>
                                        <p>Vehicles Connected</p>
                                    </div>
                                    <div className="stat-item">
                                        <h3>15+</h3>
                                        <p>Cities Covered</p>
                                    </div>
                                </div>
                            </Slide>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* Projects Grid */}
            <section className="projects-grid py-5">
                <Container>
                    <Row>
                        {projectsData.map((project, index) => (
                            <Col lg={6} className="mb-4" key={project.id}>
                                <Slide bottom delay={index * 100}>
                                    <Card className="project-card h-100 shadow-sm">
                                        <Row className="g-0">
                                            <Col md={5}>
                                                <div className="project-image-wrapper">
                                                    <img 
                                                        src={project.image} 
                                                        className="project-image"
                                                        alt={project.title}
                                                        onError={(e) => {
                                                            e.target.src = "https://via.placeholder.com/400x250/cccccc/ffffff?text=Project+Image";
                                                        }}
                                                    />
                                                    <div className="project-overlay">
                                                        <Badge 
                                                            bg={getStatusColor(project.status)} 
                                                            className="status-badge"
                                                        >
                                                            {project.status}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </Col>
                                            <Col md={7}>
                                                <Card.Body className="d-flex flex-column h-100">
                                                    <div className="mb-2">
                                                        <Badge bg="outline-primary" className="category-badge">
                                                            {project.category}
                                                        </Badge>
                                                    </div>
                                                    <Card.Title className="project-title">{project.title}</Card.Title>
                                                    <Card.Text className="project-description">
                                                        {project.description}
                                                    </Card.Text>
                                                    
                                                    <div className="project-info mb-3">
                                                        <div className="info-item">
                                                            <strong>Location:</strong> {project.location}
                                                        </div>
                                                        <div className="info-item">
                                                            <strong>Duration:</strong> {project.duration}
                                                        </div>
                                                        <div className="info-item">
                                                            <strong>Beneficiaries:</strong> {project.beneficiaries}
                                                        </div>
                                                    </div>

                                                    <div className="progress-section mb-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <small className="text-muted">Progress</small>
                                                            <small className="fw-bold">{project.progress}%</small>
                                                        </div>
                                                        <ProgressBar 
                                                            now={project.progress} 
                                                            variant={getProgressColor(project.progress)}
                                                            className="custom-progress"
                                                        />
                                                    </div>

                                                    <div className="mt-auto">
                                                        <Button variant="outline-primary" className="project-btn">
                                                            View Details
                                                        </Button>
                                                    </div>
                                                </Card.Body>
                                            </Col>
                                        </Row>
                                    </Card>
                                </Slide>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section>

            {/* Timeline Section */}
            <section className="projects-timeline py-5 bg-light">
                <Container>
                    <Row>
                        <Col lg={8} className="mx-auto text-center mb-5">
                            <h2>Project Timeline</h2>
                            <p className="lead">Our journey in transforming Philippine transportation</p>
                        </Col>
                    </Row>
                    <Row>
                        <Col lg={10} className="mx-auto">
                            <div className="timeline">
                                <div className="timeline-item">
                                    <div className="timeline-marker"></div>
                                    <div className="timeline-content">
                                        <h5>2023 - Foundation Year</h5>
                                        <p>Launched pilot programs in Metro Manila and began expansion to major cities.</p>
                                    </div>
                                </div>
                                <div className="timeline-item">
                                    <div className="timeline-marker"></div>
                                    <div className="timeline-content">
                                        <h5>2024 - Scale & Growth</h5>
                                        <p>Expanded to 15+ cities and integrated advanced analytics and AI features.</p>
                                    </div>
                                </div>
                                <div className="timeline-item">
                                    <div className="timeline-marker active"></div>
                                    <div className="timeline-content">
                                        <h5>2025 - Innovation Focus</h5>
                                        <p>Implementing sustainable transport solutions and emergency response systems.</p>
                                    </div>
                                </div>
                                <div className="timeline-item">
                                    <div className="timeline-marker future"></div>
                                    <div className="timeline-content">
                                        <h5>2026 - Nationwide Coverage</h5>
                                        <p>Complete nationwide implementation with rural connectivity initiatives.</p>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Container>
            </section>

            {/* CTA Section */}
            <section className="projects-cta py-5 bg-primary text-white">
                <Container>
                    <Row>
                        <Col lg={8} className="mx-auto text-center">
                            <Slide bottom>
                                <h2>Want to Partner With Us?</h2>
                                <p className="lead mb-4">
                                    Join us in revolutionizing transportation infrastructure across the Philippines.
                                </p>
                                <Button variant="light" size="lg" className="me-3">
                                    Become a Partner
                                </Button>
                                <Button variant="outline-light" size="lg">
                                    Download Case Studies
                                </Button>
                            </Slide>
                        </Col>
                    </Row>
                </Container>
            </section>
        </div>
    );
};

export default Projects;